import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import { Button } from "../components/ui/button";
import { fmtTime } from "../lib/format";
import { Headphones, Lock, Play, CheckCircle2, Crown, Volume2 } from "lucide-react";
import { toast } from "sonner";

export default function AudioLibrary() {
  const { user } = useAuth();
  const { play, current } = usePlayer();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const load = () => api.get("/audio/lessons").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onPlay = (it) => {
    if (it.locked) { navigate("/upgrade"); return; }
    if (!it.has_audio) { toast.info("Audio for this lesson hasn't been uploaded yet."); return; }
    play(it);
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-2">
          <Headphones className="h-7 w-7 text-amber-600" />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A192F]">Audio Library</h1>
        </div>
        <p className="text-slate-500 mb-8">Narrated lessons with resume playback. Your position is saved automatically.</p>

        {!user?.has_full_access && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-6 flex items-center justify-between gap-3" data-testid="audio-upgrade-banner">
            <span className="text-sm text-amber-800">Free accounts can preview sample audio. Unlock the full library with Full Access.</span>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white shrink-0" onClick={() => navigate("/upgrade")}><Crown className="h-4 w-4 mr-1" /> $49.95</Button>
          </div>
        )}

        <div className="space-y-3" data-testid="audio-list">
          {items.map((it) => {
            const active = current?.id === it.id;
            const pct = it.duration ? Math.min(100, (it.position / it.duration) * 100) : 0;
            return (
              <div key={it.id} className={`rounded-xl border p-4 ${active ? "border-amber-400 bg-amber-50/40" : it.locked ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white hover:border-amber-300 transition"}`} data-testid={`audio-item-${it.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <button onClick={() => onPlay(it)} className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 ${it.locked ? "bg-slate-200" : "bg-[#0A192F] hover:bg-amber-600 transition"}`} data-testid={`audio-play-${it.id}`}>
                      {it.locked ? <Lock className="h-5 w-5 text-slate-500" /> : <Play className="h-5 w-5 text-amber-500 ml-0.5" />}
                    </button>
                    <div className="min-w-0">
                      <div className="font-medium text-[#0A192F] truncate flex items-center gap-2">
                        {it.title}
                        {it.completed && <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-2">
                        {it.is_sample && <span className="text-emerald-600 font-semibold">SAMPLE</span>}
                        {it.has_audio ? <span className="font-mono">{fmtTime(it.duration)}</span> : <span className="italic text-slate-400 flex items-center gap-1"><Volume2 className="h-3 w-3" /> Audio pending</span>}
                      </div>
                    </div>
                  </div>
                </div>
                {it.position > 0 && !it.completed && (
                  <div className="mt-3 h-1 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-amber-500" style={{ width: `${pct}%` }} /></div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
